/* ==========================================================================
   BANDO · theme.js
   Header, Mobile Menu, Cart Drawer, Cart AJAX updates.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Mobile menu ----------------------------------------------- */
  const menu = document.querySelector('[data-mobile-menu]');
  const menuOpenBtns = document.querySelectorAll('[data-menu-open]');
  const menuCloseBtns = document.querySelectorAll('[data-menu-close]');

  function setMenuState(open) {
    if (!menu) return;
    menu.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) menu.querySelector('a, button')?.focus();
    else document.querySelector('[data-menu-open]')?.focus();
  }
  menuOpenBtns.forEach(b => b.addEventListener('click', () => setMenuState(true)));
  menuCloseBtns.forEach(b => b.addEventListener('click', () => setMenuState(false)));

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (menu?.getAttribute('aria-hidden') === 'false') setMenuState(false);
    if (drawer?.getAttribute('aria-hidden') === 'false') closeCart();
    closeAllDropdowns();
  });

  /* ---------- Header dropdown menus (desktop) -------------------------- */
  const dropdownToggles = document.querySelectorAll('[data-dropdown-toggle]');

  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-dropdown-toggle]').forEach(t => {
      if (t !== except) t.setAttribute('aria-expanded', 'false');
    });
  }

  dropdownToggles.forEach(toggle => {
    toggle.addEventListener('click', (e) => {
      // If it's a link (a tag), allow navigation — don't prevent default
      if (toggle.tagName === 'A') return;
      e.preventDefault();
      e.stopPropagation();
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      closeAllDropdowns(toggle);
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
    // Open dropdown on hover for link-style toggles
    const parent = toggle.closest('[data-dropdown]');
    if (parent) {
      parent.addEventListener('mouseenter', () => {
        toggle.setAttribute('aria-expanded', 'true');
      });
      parent.addEventListener('mouseleave', () => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* Click outside closes dropdowns */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-dropdown]')) closeAllDropdowns();
  });

  /* ---------- Mobile menu accordion groups ----------------------------- */
  document.querySelectorAll('[data-mobile-group-toggle]').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });

  /* ---------- Sticky-header shadow on scroll --------------------------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* =====================================================================
     CART DRAWER
     ===================================================================== */
  const drawer = document.getElementById('cart-drawer');
  const cartCountEl = document.querySelector('[data-cart-count]');

  function openCart() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => drawer.querySelector('.cart-drawer__panel')?.focus(), 50);
    refreshDrawer().catch(() => {});
  }
  function closeCart() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* Intercept cart icon clicks (open drawer instead of navigating) */
  document.querySelectorAll('a[href$="/cart"], a[href*="/cart?"]').forEach(el => {
    if (el.closest('.cart-drawer')) return;
    el.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      openCart();
    });
  });

  /* Close triggers via delegation */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeCart();
    if (e.target.closest('[data-cart-close-on-click]')) setTimeout(closeCart, 100);
  });

  /* ---------- Cart API helpers ----------------------------------------- */
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }
  async function updateCartLine(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    return res.json();
  }
  async function addToCart(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Fehler beim Hinzufügen');
    }
    return res.json();
  }

  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]);
  }

  function rebuildDrawerFromJSON(cart) {
    const body = document.querySelector('[data-cart-body]');
    const foot = document.querySelector('[data-cart-foot]');
    if (!body || !foot) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <p class="lead" style="font-size: var(--fs-base); margin-bottom: var(--s-4);">Dein Warenkorb ist leer.</p>
          <a href="/collections/all" class="btn btn--secondary">Zum Sortiment</a>
        </div>`;
      foot.innerHTML = '';
    } else {
      let html = '<ul class="cart-items" role="list">';
      cart.items.forEach((item, idx) => {
        const hasVariant = item.variant_title && !item.variant_title.includes('Default');
        const imgSrc = item.image ? item.image.replace(/_\d+x\d*\./, '_160x.') : null;
        html += `
          <li class="ci" data-cart-line="${idx+1}" data-key="${escapeHtml(item.key)}">
            <a href="${item.url}" class="ci__img${imgSrc ? '' : ' ci__img--empty'}" data-cart-close-on-click>
              ${imgSrc ? `<img src="${imgSrc}" alt="" width="80" height="80">` : ''}
            </a>
            <div class="ci__body">
              <a href="${item.url}" class="ci__title" data-cart-close-on-click>${escapeHtml(item.product_title)}</a>
              ${hasVariant ? `<span class="ci__variant">${escapeHtml(item.variant_title)}</span>` : ''}
              <div class="ci__bottom">
                <div class="ci__qty">
                  <button type="button" class="ci__qty-btn" data-cart-qty-change="${escapeHtml(item.key)}" data-qty-delta="-1">−</button>
                  <span class="ci__qty-value">${item.quantity}</span>
                  <button type="button" class="ci__qty-btn" data-cart-qty-change="${escapeHtml(item.key)}" data-qty-delta="1">+</button>
                </div>
                <span class="ci__price">${formatMoney(item.final_line_price)}</span>
              </div>
              <button type="button" class="ci__remove" data-cart-remove="${escapeHtml(item.key)}" style="align-self:flex-start;margin-top:2px;">Entfernen</button>
            </div>
          </li>`;
      });
      html += '</ul>';
      body.innerHTML = html;

      // Stored price = Netto. Brutto = Netto * 1.19
      const netCents    = cart.total_price;
      const mwstCents   = Math.round(netCents * 0.19);
      const bruttoCents = Math.round(netCents * 1.19);
      const freeShip    = 9900;
      const remaining   = Math.max(0, freeShip - netCents);
      const shipPct     = Math.min(100, Math.round(netCents / freeShip * 100));
      const shipHtml    = remaining > 0
        ? `<div class="cart-drawer__progress-text mono" style="font-size:0.8em;">Noch <strong>${formatMoney(remaining)}</strong> bis kostenloser Versand</div>
           <div class="cart-drawer__progress-bar"><div class="cart-drawer__progress-fill" style="width:${shipPct}%"></div></div>`
        : `<div class="cart-drawer__progress-text mono" style="font-size:0.8em;color:#2e7d32;">✓ Kostenloser Versand</div>
           <div class="cart-drawer__progress-bar"><div class="cart-drawer__progress-fill" style="width:100%"></div></div>`;

      // Staffelrabatt-Indikator
      const disc = netCents >= 100000 ? 10 : netCents >= 50000 ? 5 : 0;
      let discHtml = '';
      if (disc >= 10) {
        discHtml = `<div style="font-family:'JetBrains Mono',monospace;font-size:0.78em;color:#E53935;padding:6px 0 2px;">● −10% Staffelrabatt aktiv</div>`;
      } else if (disc >= 5) {
        const missingTo10 = 100000 - netCents;
        const pctTo10 = Math.min(100, Math.round((netCents - 50000) / 50000 * 100));
        discHtml = `<div style="font-family:'JetBrains Mono',monospace;font-size:0.78em;color:#E53935;padding:6px 0 2px;">● −5% Staffelrabatt aktiv</div>`
                 + `<div style="height:3px;background:#e0e0e0;margin:4px 0;overflow:hidden;"><div style="height:100%;width:${pctTo10}%;background:#E53935;transition:width 300ms;"></div></div>`
                 + `<div style="font-family:'JetBrains Mono',monospace;font-size:0.75em;color:#6C7680;padding-bottom:4px;">Noch ${formatMoney(missingTo10)} bis −10%</div>`;
      } else if (netCents > 0) {
        const missingTo5 = 50000 - netCents;
        const pctTo5 = Math.min(100, Math.round(netCents / 50000 * 100));
        discHtml = `<div style="height:3px;background:#e0e0e0;margin:6px 0 4px;overflow:hidden;"><div style="height:100%;width:${pctTo5}%;background:#E53935;transition:width 300ms;"></div></div>`
                 + `<div style="font-family:'JetBrains Mono',monospace;font-size:0.75em;color:#6C7680;padding-bottom:4px;">Noch ${formatMoney(missingTo5)} bis −5% Staffelrabatt</div>`;
      }

      foot.innerHTML = `
        <div style="padding:0 0 10px;">${shipHtml}${discHtml}</div>
        <div class="cart-drawer__totals">
          <div class="cart-drawer__total-row">
            <span class="mono">Zwischensumme (Netto)</span>
            <span class="cart-drawer__total-value">${formatMoney(netCents)}</span>
          </div>
          <div class="cart-drawer__total-row cart-drawer__total-row--muted">
            <span class="mono">zzgl. MwSt. 19%</span>
            <span class="mono">${formatMoney(mwstCents)}</span>
          </div>
          <div class="cart-drawer__total-row" style="font-weight:700;border-top:1px solid #e0e0e0;padding-top:8px;margin-top:4px;">
            <span class="mono">Gesamt (Brutto)</span>
            <span class="cart-drawer__total-value">${formatMoney(bruttoCents)}</span>
          </div>
        </div>
        <div class="cart-drawer__actions">
          <a href="/cart" class="btn btn--secondary" style="flex:1;">Warenkorb</a>
          <a href="/checkout" class="btn btn--accent" style="flex:2;">Zur Kasse →</a>
        </div>
        <div class="cart-drawer__note mono">Versand &amp; Zahlungsmethoden im nächsten Schritt</div>
      `;
    }

    // Count badge + text
    if (cartCountEl) {
      cartCountEl.textContent = cart.item_count;
      cartCountEl.style.display = cart.item_count > 0 ? '' : 'none';
    }
    const countText = document.querySelector('[data-cart-count-text]');
    if (countText) countText.textContent = cart.item_count;
  }

  async function refreshDrawer() {
    try {
      const cart = await fetchCart();
      rebuildDrawerFromJSON(cart);
      // Produktseite über Warenkorb-Update informieren
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      // Cross-Sell separat aktualisieren (liegt außerhalb data-cart-body)
      refreshCrossSell().catch(() => {});
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }

  /* ---------- Cross-Sell AJAX refresh ---------------------------------- */
  // Shopify Section Rendering API: /?sections=cart-crosssell
  // gibt sections/cart-crosssell.liquid gerendert mit aktuellem Cart zurück.
  // Nur der Wrapper-Div [data-cart-crosssell] wird ersetzt —
  // niemals data-cart-body oder data-cart-foot.
  async function refreshCrossSell() {
    const wrap = document.querySelector('[data-cart-crosssell]');
    if (!wrap) return;
    try {
      const res = await fetch('/?sections=cart-crosssell', {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return;
      const json = await res.json();
      // Shopify wraps section content in a <div id="shopify-section-..."> automatically
      const html = json['cart-crosssell'];
      if (html !== undefined) wrap.innerHTML = html;
    } catch (e) {
      // silent fail — cross-sell ist nicht kritisch
    }
  }

  // Globale BandoCart API — von Product-Page und Cross-Sell verwendet
  window.BandoCart = window.BandoCart || {};
  window.BandoCart.refresh = refreshDrawer;
  window.BandoCart.refreshCrossSell = refreshCrossSell;
  window.BandoCart.open = openCart;

  /* ---------- Cart line qty/remove (delegated) ------------------------- */
  document.addEventListener('click', async (e) => {
    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const key = qtyBtn.dataset.cartQtyChange;
      const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
      const current = parseInt(qtyBtn.parentElement.querySelector('.ci__qty-value').textContent, 10) || 0;
      qtyBtn.disabled = true;
      try {
        await updateCartLine(key, Math.max(0, current + delta));
        await refreshDrawer();
      } catch (err) { console.error(err); }
      finally { qtyBtn.disabled = false; }
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      // Bestätigung einholen
      showRemoveConfirm(removeBtn.dataset.cartRemove);
      return;
    }
    // Qty -1 in drawer → wenn qty=1, auch Bestätigung
    const qtyBtn2 = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn2 && parseInt(qtyBtn2.dataset.qtyDelta, 10) < 0) {
      const cur = parseInt(qtyBtn2.parentElement.querySelector('.ci__qty-value').textContent, 10) || 0;
      if (cur <= 1) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showRemoveConfirm(qtyBtn2.dataset.cartQtyChange);
        return;
      }
    }
  });

  /* ---------- Intercept product form submits --------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"], [name="add"]');
    const orig = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = '… wird hinzugefügt'; }
    try {
      await addToCart(new FormData(form));
      await refreshDrawer();
      openCart();
      if (btn) { btn.textContent = '✓ Hinzugefügt'; }
      // Qty-Feld auf aktuellem Warenkorb-Stand belassen und Cart-Key merken
      const cartData = await fetchCart();
      const variantId = form.querySelector('input[name="id"]')?.value;
      const cartItem = variantId ? cartData.items.find(i => String(i.variant_id) === String(variantId)) : null;
      if (cartItem) {
        const qtyEl = document.querySelector('[data-qty-input]');
        if (qtyEl) { qtyEl.value = cartItem.quantity; qtyEl.dataset.cartKey = cartItem.key; qtyEl.dispatchEvent(new Event('input')); }
      }
      if (window.BANDO_refreshCart) window.BANDO_refreshCart();
      setTimeout(() => { if (btn) { btn.textContent = orig; btn.disabled = false; } }, 1500);
    } catch (err) {
      console.error(err);
      if (btn) { btn.textContent = 'Fehler — erneut versuchen'; }
      setTimeout(() => { if (btn) { btn.textContent = orig; btn.disabled = false; } }, 2500);
    }
  });

  /* ---------- Initial cart count load ---------------------------------- */
  if (cartCountEl) {
    fetchCart().then(cart => {
      cartCountEl.textContent = cart.item_count;
      cartCountEl.style.display = cart.item_count > 0 ? '' : 'none';
    }).catch(() => {});
  }


  /* ---------- Remove confirmation dialog -------------------------------- */
  function showRemoveConfirm(cartKey) {
    const existing = document.getElementById('bando-remove-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.id = 'bando-remove-modal';
    modal.innerHTML = `
      <div class="brm-overlay"></div>
      <div class="brm-dialog" role="dialog" aria-modal="true" aria-labelledby="brm-title">
        <p class="brm-text" id="brm-title">Artikel wirklich aus dem Warenkorb entfernen?</p>
        <div class="brm-btns">
          <button class="brm-btn brm-btn--cancel" id="brm-nein">Nein, behalten</button>
          <button class="brm-btn brm-btn--confirm" id="brm-ja">Ja, entfernen</button>
        </div>
      </div>`;

    document.body.appendChild(modal);

    const close = () => modal.remove();
    modal.querySelector('#brm-nein').addEventListener('click', close);
    modal.querySelector('.brm-overlay').addEventListener('click', close);
    modal.querySelector('#brm-ja').addEventListener('click', async () => {
      close();
      try {
        await updateCartLine(cartKey, 0);
        await refreshDrawer();
      } catch (err) { console.error(err); }
    });

    // Inject styles once
    if (!document.getElementById('bando-remove-modal-css')) {
      const style = document.createElement('style');
      style.id = 'bando-remove-modal-css';
      style.textContent = `
        #bando-remove-modal { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .brm-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.45); }
        .brm-dialog { position: relative; background: #fff; padding: 24px; max-width: 320px; width: 90%; box-shadow: 0 8px 32px rgba(0,0,0,0.18); }
        .brm-text { font-family: 'JetBrains Mono', monospace; font-size: 13px; line-height: 1.5; margin-bottom: 20px; }
        .brm-btns { display: flex; gap: 10px; }
        .brm-btn { flex: 1; padding: 10px; font-size: 13px; cursor: pointer; border: 1.5px solid #000; background: #fff; font-family: inherit; }
        .brm-btn--confirm { background: #000; color: #fff; }
        .brm-btn--confirm:hover { background: #E53935; border-color: #E53935; }
        .brm-btn--cancel:hover { background: #f5f5f5; }
      `;
      document.head.appendChild(style);
    }
  }

  /* ---------- Expose ---------------------------------------------------- */
  window.BANDO = window.BANDO || {};
  window.BANDO.openCart = openCart;
  window.BANDO.closeCart = closeCart;
  window.BANDO.refreshDrawer = refreshDrawer;


  /* NB Toggle removed */


  /* ---------- Flyout Dropdown — Unterkategorien aus data-subs ----------- */
  (function() {

    document.querySelectorAll('.header__flyout').forEach(function(flyout) {
      var cats  = flyout.querySelectorAll('.header__flyout-cat');
      var panel = flyout.querySelector('[data-flyout-panel]');
      if (!cats.length || !panel) return;

      var hoverTimer = null;

      function buildPanel(cat) {
        cats.forEach(function(c) { c.classList.remove('is-hovered'); });
        cat.classList.add('is-hovered');

        var title  = cat.dataset.catTitle || '';
        var catUrl = cat.getAttribute('href') || '';

        // Echte Shopify-Unterkategorien aus data-subs lesen
        var subs = [];
        try {
          var raw = (cat.dataset.subs || '').trim();
          if (raw && raw !== '[]' && raw !== '') {
            subs = JSON.parse(raw).map(function(s) { return [s.t, s.u]; });
          }
        } catch(e) { console.warn('flyout data-subs parse error', e); }

        if (!subs.length) {
          panel.innerHTML = '<div class="header__flyout-hint">'
            + '<span class="header__flyout-hint-text">Alle ' + title + ' ansehen</span></div>';
          return;
        }

        var html = '<div class="header__flyout-subpanel">'
          + '<div class="header__flyout-panel-title">'
          + '<span>' + (title || '').toUpperCase() + '</span>'
          + '<a href="' + catUrl + '">Alle →</a>'
          + '</div>'
          + '<div class="header__flyout-subcats">';

        subs.forEach(function(sub) {
          var name = sub[0], url = sub[1] || catUrl;
          html += '<a href="' + url + '" class="header__flyout-subcat" style="padding-left:18px;">'
            + '<span class="header__flyout-subcat-name">' + name + '</span>'
            + '<span class="header__flyout-subcat-arrow">›</span>'
            + '</a>';
        });

        html += '</div></div>';
        panel.innerHTML = html;
      }

      cats.forEach(function(cat) {
        cat.addEventListener('mouseenter', function() {
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(function() { buildPanel(cat); }, 100);
        });
        cat.addEventListener('mouseleave', function() {
          clearTimeout(hoverTimer);
        });
      });
    });
  })();

  /* =====================================================================
     PRODUCT GALLERY — Thumbnail switcher
     ===================================================================== */
  function initGallery() {
    document.querySelectorAll('.pdp-gallery').forEach(gallery => {
      const slides = gallery.querySelectorAll('.pdp-gallery__slide');
      const thumbs = gallery.querySelectorAll('.pdp-gallery__thumb');

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const id = thumb.dataset.thumbId;

          slides.forEach(slide => {
            const active = slide.dataset.mediaId === id;
            slide.classList.toggle('is-active', active);
            if (active) slide.removeAttribute('hidden');
            else slide.setAttribute('hidden', '');
          });

          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallery);
  } else {
    initGallery();
  }

})();

  /* ═══════════════════════════════════════════════════════════════
     MERKZETTEL — localStorage Wishlist
     ═══════════════════════════════════════════════════════════════ */
  (function() {
    var KEY = 'bando_wishlist';

    function getList()     { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
    function saveList(l)   { localStorage.setItem(KEY, JSON.stringify(l)); updateBadge(); }
    function isInList(h)   { return getList().indexOf(h) > -1; }
    function toggleItem(h) {
      var l = getList();
      var i = l.indexOf(h);
      if (i > -1) { l.splice(i, 1); } else { l.push(h); }
      saveList(l);
      return i === -1; // true = added
    }

    function updateBadge() {
      var count = getList().length;
      var badge = document.getElementById('wl-badge');
      if (!badge) return;
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }

    function initButtons() {
      document.querySelectorAll('[data-wishlist]').forEach(function(btn) {
        var handle = btn.dataset.wishlist;
        if (!handle) return;
        // Set initial state
        btn.classList.toggle('is-saved', isInList(handle));

        // Prevent duplicate listeners
        if (btn._wlInit) return;
        btn._wlInit = true;

        btn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          var added = toggleItem(handle);
          btn.classList.toggle('is-saved', added);

          // Sync all buttons for same product
          document.querySelectorAll('[data-wishlist="' + handle + '"]').forEach(function(b) {
            b.classList.toggle('is-saved', added);
          });

          // Mini-Feedback: Pulse-Animation
          btn.classList.add('is-pulse');
          setTimeout(function() { btn.classList.remove('is-pulse'); }, 300);
        });
      });
    }

    // Init on load + after dynamic content changes
    initButtons();
    document.addEventListener('bando:products-loaded', initButtons);
    updateBadge();

    // Re-init after cart drawer updates
    var observer = new MutationObserver(function() { initButtons(); updateBadge(); });
    var root = document.getElementById('cart-drawer') || document.body;
    observer.observe(root, { childList: true, subtree: true });
  })();



/* ═══════════════════════════════════════════════════════════════
   CART ICON — Hover öffnet Drawer (zusätzlich zu Click)
   ═══════════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', function() {
  var cartIcon = document.querySelector('[data-cart-icon]');
  var cartDrawer = document.getElementById('cart-drawer');
  var cartPanel = cartDrawer ? cartDrawer.querySelector('.cart-drawer__panel') : null;

  if (cartIcon && window.BANDO && window.BANDO.openCart) {
    var cartHoverTimer;
    var cartCloseTimer;

    function scheduleCartClose() {
      clearTimeout(cartCloseTimer);
      cartCloseTimer = setTimeout(function() {
        if (window.BANDO.closeCart) window.BANDO.closeCart();
      }, 400);
    }
    function cancelCartClose() {
      clearTimeout(cartCloseTimer);
    }

    // Open on hover over cart icon
    cartIcon.addEventListener('mouseenter', function() {
      clearTimeout(cartHoverTimer);
      cancelCartClose();
      cartHoverTimer = setTimeout(function() {
        window.BANDO.openCart();
      }, 200);
    });
    cartIcon.addEventListener('mouseleave', function() {
      clearTimeout(cartHoverTimer);
      scheduleCartClose();
    });

    // Keep open when mouse moves into the panel
    if (cartPanel) {
      cartPanel.addEventListener('mouseenter', cancelCartClose);
      cartPanel.addEventListener('mouseleave', scheduleCartClose);
    }
  }

  /* ─── MERKLISTE nav-link → /pages/favoriten ─── */
  document.querySelectorAll('a.header__nav-link, a.mobile-menu__nav-link').forEach(function(a) {
    if (a.textContent.trim().toLowerCase() === 'merkliste') {
      a.href = '/pages/favoriten';
    }
  });

  /* ─── WISHLIST POPOVER — Hover auf Herz-Icon ─── */
  var wlWrap = document.querySelector('[data-wl-wrap]');
  var wlPop  = document.getElementById('wl-pop');
  if (!wlWrap || !wlPop) return;

  var KEY = 'bando_wishlist';
  var popLoaded = false;
  var hideTimer, showTimer;

  function openPop() {
    clearTimeout(hideTimer);
    wlPop.classList.add('is-open');
    wlPop.setAttribute('aria-hidden', 'false');
    if (!popLoaded) { loadPopItems(); popLoaded = true; }
  }
  function closePop() {
    clearTimeout(showTimer);
    wlPop.classList.remove('is-open');
    wlPop.setAttribute('aria-hidden', 'true');
  }

  wlWrap.addEventListener('mouseenter', function() {
    clearTimeout(hideTimer);
    showTimer = setTimeout(openPop, 180);
  });
  wlWrap.addEventListener('mouseleave', function() {
    clearTimeout(showTimer);
    hideTimer = setTimeout(closePop, 200);
  });

  function fmtPrice(cents) { return (cents / 100).toFixed(2).replace('.', ',') + ' €'; }

  function loadPopItems() {
    var handles = JSON.parse(localStorage.getItem(KEY) || '[]');
    var countEl = document.getElementById('wl-pop-count');
    var itemsEl = document.getElementById('wl-pop-items');
    if (!handles.length) {
      if (countEl) countEl.textContent = '0 Produkte';
      if (itemsEl) itemsEl.innerHTML = '<p class="wl-pop__empty">Noch keine Produkte gespeichert.</p>';
      return;
    }
    if (countEl) countEl.textContent = handles.length + ' Produkt' + (handles.length === 1 ? '' : 'e');

    var loaded = 0, products = {};
    // Show max 4 in popover
    var preview = handles.slice(0, 4);
    preview.forEach(function(handle) {
      fetch('/products/' + handle + '.js')
        .then(function(r) { return r.json(); })
        .then(function(p) { products[handle] = p; loaded++; if (loaded === preview.length) renderPop(preview, products); })
        .catch(function() { loaded++; if (loaded === preview.length) renderPop(preview, products); });
    });
  }

  function renderPop(handles, products) {
    var itemsEl = document.getElementById('wl-pop-items');
    if (!itemsEl) return;
    var html = '';
    handles.forEach(function(handle) {
      var p = products[handle]; if (!p) return;
      var img = p.featured_image || (p.images && p.images[0] ? p.images[0].src : '');
      var price = p.variants && p.variants[0] ? fmtPrice(p.variants[0].price) : '—';
      html += '<a href="/products/' + p.handle + '" class="wl-pop-item">'
        + (img ? '<img src="' + img + '" class="wl-pop-item__img" alt="' + p.title + '" loading="lazy">' : '<div class="wl-pop-item__img"></div>')
        + '<div class="wl-pop-item__info">'
        + '<div class="wl-pop-item__title">' + p.title + '</div>'
        + '<div class="wl-pop-item__price">' + price + '</div>'
        + '</div></a>';
    });
    itemsEl.innerHTML = html || '<p class="wl-pop__empty">Noch keine Produkte gespeichert.</p>';
  }
});

/* ── Kollektions-Tile Hover Image Cycling ── */
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('[data-tile-cycle]').forEach(function(tile) {
    var hoverImgs = tile.querySelectorAll('.tile__img-hover');
    var mainImg = tile.querySelector('.tile__img-main');
    if (!hoverImgs.length || !mainImg) return;

    var timer, idx = 0;

    tile.addEventListener('mouseenter', function() {
      mainImg.style.opacity = '0';
      idx = 0;
      showNext();
      timer = setInterval(showNext, 700);
    });

    tile.addEventListener('mouseleave', function() {
      clearInterval(timer);
      hoverImgs.forEach(function(img) { img.style.opacity = '0'; });
      mainImg.style.opacity = '1';
      idx = 0;
    });

    function showNext() {
      hoverImgs.forEach(function(img) { img.style.opacity = '0'; });
      if (hoverImgs[idx]) hoverImgs[idx].style.opacity = '1';
      idx = (idx + 1) % hoverImgs.length;
    }
  });
});
